import { AppItineraryOrNode, PickupInformationExtended } from "@/types/otp/+extended";
import { dateToHoursAndMinutesFormatter } from "./date-formatters";

/**
 * Converts seconds since midnight (0-86400) to a formatted 12-hour time string (hh:mm AM/PM).
 * Returns "N/A" if the input is not a valid number.
 */
export function secondsSinceMidnightToHoursAndMinutesFormatter(
    seconds?: number | null
): string {
    if (typeof seconds !== "number" || Number.isNaN(seconds)) return "N/A";

    const normalized = ((seconds % 86400) + 86400) % 86400;
    const hours24 = Math.floor(normalized / 3600);
    const minutes = Math.floor((normalized % 3600) / 60);
    const period = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    const paddedHours = String(hours12).padStart(2, "0");
    const paddedMinutes = String(minutes).padStart(2, "0");

    return `${paddedHours}:${paddedMinutes} ${period}`;
}

/**
 * Formats the pickup service's availability as a time range or single time string.
 * If both start and end are valid, returns "start – end".
 * If only one is valid, returns that value.
 * Returns null if neither are available or valid.
 */
export function formatServiceAvailabilityRange(
    pickup?: PickupInformationExtended | null
): string | null {
    if (!pickup) return null;

    const { prior_notice_start_time, prior_notice_last_time } = pickup;

    const start =
        typeof prior_notice_start_time === "number"
            ? secondsSinceMidnightToHoursAndMinutesFormatter(prior_notice_start_time)
            : null;

    const end =
        typeof prior_notice_last_time === "number"
            ? secondsSinceMidnightToHoursAndMinutesFormatter(prior_notice_last_time)
            : null;

    if (start && start !== "N/A" && end && end !== "N/A") {
        return `${start} - ${end}`;
    }
    if (start && start !== "N/A") return start;
    if (end && end !== "N/A") return end;

    return null;
}

/**
 * Determines the best label and value for itinerary timing.
 * Prefers service availability from pickup info over trip times.
 * Returns null if no meaningful label/value can be resolved.
 */
export function getItineraryTimeLabel(params: {
    startTime?: number | null;
    endTime?: number | null;
    legs?: AppItineraryOrNode["legs"] | null;
}): { label: string; value: string } | null {
    const { startTime, endTime, legs } = params;

    const start = dateToHoursAndMinutesFormatter(startTime);
    const startIsAvailable = start && start !== "N/A";
    if (startIsAvailable) {
        return { label: "Estimated Departure", value: start };
    }

    const end = dateToHoursAndMinutesFormatter(endTime);
    const endIsAvailable = end && end !== "N/A";
    if (endIsAvailable) {
        return { label: "Estimated Arrival", value: end };
    }

    if (legs) {
        for (const leg of legs) {
            const pickup =
                (leg &&
                    typeof leg === "object" &&
                    "extended" in leg &&
                    leg.extended &&
                    typeof leg.extended === "object" &&
                    "pickupInformation" in leg.extended)
                    ? (leg.extended as { pickupInformation?: PickupInformationExtended })
                          .pickupInformation
                    : undefined;

            const range = formatServiceAvailabilityRange(pickup);
            if (range) {
                return { label: "Estimated Pickup Time", value: range };
            }
        }
    }

    return null;
}
